import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { RULE_MESSAGE_TYPE, RULE_STATUS, RULE_UPDATED_EVENT } from './model/constants';
import { RuleCommentModel, RuleModel, RuleStipModel } from './model/rule.model';
import { RuleService } from './service/rule.service';
import { Store } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderDetailModel } from '../state/model/order-detail.model';
import { QualityState } from '../state/quality.state';
import { BroadcastService } from '@cv-invision/core';

@Component({
	selector: 'cv-rule-component',
	templateUrl: './rule.component.html',
	styleUrls: ['./rule.component.scss']
})
export class RuleComponent implements OnInit, AfterViewInit, OnDestroy {
	orderDetail$: Observable<OrderDetailModel> = this.store.select(QualityState.getOrderDetail);
	@Input() rule: RuleModel;
	@Input() status: RULE_STATUS;
	@Input() active: boolean;
	@Input() disabledActions: boolean;
	@Input() viewOnlyUncheckedRules: boolean;
	@Output() selected: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() viewComments: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() viewStips: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() addComment: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() addStip: EventEmitter<RuleModel> = new EventEmitter<RuleModel>();
	@Output() updateStatus: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('commentRef') commentRef: ElementRef;
	@ViewChild('shadowRef') shadowRef!: ElementRef;
	private atComponentDestroy = new Subject();
	public RULE_STATUS = RULE_STATUS;
	public RULE_MESSAGE_TYPE = RULE_MESSAGE_TYPE;
	public type: RULE_MESSAGE_TYPE;
	public title: string;
	public description: string;
	public ruleSet: string;
	public category: string;
	resolver: any;
	public notes: RuleCommentModel[];
	public stips: RuleStipModel[];
	public orderDetailModel: OrderDetailModel;
	public updating_status: RULE_STATUS;
	public hideRule = false;
	public ruleId: string;
	public ruleClass: string;
	public ruleStatus: RULE_STATUS;
	public isAI = false;
	public isFeedbackOpen = false;
	public showToggle = false;
	public isExpanded = false;
	public shouldClamp = false;
	readonly CLAMP_HEIGHT = 34;

	get vendorNote() {
		return this.rule?.notes?.find(n => n.vendor === true);
	}

	get commentCount(): number {
		return this.rule?.notes?.filter(n => !n.vendor).length
	}

	constructor(
		private ruleService: RuleService,
		private store: Store,
		private broadcastService: BroadcastService
	) {
		this.orderDetail$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((orderDetailModel: OrderDetailModel) => {
				this.orderDetailModel = orderDetailModel;
			});
		this.rule = {} as any;
	}
	ngAfterViewInit(): void {
		// wait for full rendering & styles to apply
		setTimeout(() => {
			const el = this.commentRef?.nativeElement;
			if (!el) return;

			const scrollHeight = el.scrollHeight;

			this.shouldClamp = scrollHeight - this.CLAMP_HEIGHT > 2;
		}, 0);
	}

	ngOnInit(): void {
		this.type = this.rule.messageType || RULE_MESSAGE_TYPE.Info;
		this.title = this.rule.displayText;
		this.description = this.rule.message;
		this.ruleSet = this.rule.ruleSetDisplayText;
		this.category = this.rule.category;
		this.resolver = this.rule.resolver;
		this.notes = this.rule.notes || [];
		this.stips = this.rule.stips || [];
		this.ruleId = 'rule' + this.rule.uiSection + this.rule.displayText.replace(/\s/g, '');

		this.isAI = (this.rule && this.rule['variables']) ? this.rule['variables'].ref_ai : false;

		const sections = this.rule.uiSection.toLocaleLowerCase().split('|');

		if (sections.length) {
			this.ruleClass = 'rule-' + sections[0];

			if (sections.length > 1) {
				this.ruleClass += ' multiple-section-' + sections[1];
			}
		}

		this.ruleStatus = this.rule.status;
	}

	/**
	 * Toggles the expanded/collapsed state of the comment box
	 */
	toggleExpand(): void {
		this.isExpanded = !this.isExpanded;
	}

	/**
	 * Determines whether the "Show more / Show less" toggle should be displayed.
	 * @param note 
	 * @returns 
	 */
	shouldShowToggle(note: string): boolean {
		return note?.split('\n')?.length > 1 || note?.length > 150;
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onRuleSelected(): void {
		if (this.rule.multipleUiSections && this.rule.multipleUiSections.length <= 1) {
			const rule = Object.assign({}, this.rule);
			rule.uiSection = this.rule.multipleUiSections[0].uiSection;
			this.selected.emit(rule);
		}
	}

	/**
	 * On Rule selected over the Multiple section, emit the selected event with the current rule and the section.
	 */
	onMultipleRuleSelected(selectedUiSection: any): void {
		const rule = Object.assign({}, this.rule);
		rule.uiSection = selectedUiSection.uiSection;
		this.selected.emit(rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onViewComments(): void {
		this.viewComments.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onViewStips(): void {
		this.viewStips.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onAddComment(): void {
		this.addComment.emit(this.rule);
	}

	/**
	 * On Rule selected, emit the selected event with the current rule.
	 */
	onAddStip(): void {
		this.addStip.emit(this.rule);
	}

	/**
	 * On update status click, call endpoint to update rule. Return the result through output event.
	 * @param status: RULE_STATUS
	 */
	onUpdateStatus(status: RULE_STATUS): void {
		if (this.viewOnlyUncheckedRules) {
			this.hideRule = true;
		}

		if (this.rule.status === 0) {
			this.broadcastService.broadcast({
				name: RULE_UPDATED_EVENT,
				event: {
					rule: this.rule,
					type: 'subtract'
				}
			});
		}

		this.ruleStatus = status;

		this.updating_status = status;

		const orderId = this.orderDetailModel.id;
		const documentId = this.orderDetailModel.documentId;
		this.ruleService
			.updateRuleStatus(orderId, documentId, this.rule.name, status)
			.subscribe(result => {
				if (result.isValid) {

					this.updateStatus.emit({
						ruleName: this.rule.name,
						ruleStatus: status
					}
					);

				} else {
					this.updateStatus.emit(false);
				}

				this.updating_status = null;
			});
	}

	/**
	 * Toggles the visibility of the rule feedback section.
	 */
	onFeedbackToggle() {
		this.isFeedbackOpen = !this.isFeedbackOpen;
	}

	/**
	 * Closes the rule feedback section.
	 */
	closeFeedback() {
		this.isFeedbackOpen = false;
	}

	/**
	 * Determines the icon if the button that toggles the feedback section.
	 * @returns {string} - The button icon class.
	 */
	getFeedBackIcon() {
		return this.isFeedbackOpen ? "fa-light fa-chevron-up" : "fa-light fa-lightbulb-exclamation";
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
